import React, { useState, useEffect } from 'react';
import BlogCard from '../components/BlogCard';

function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [postReadTimes, setPostReadTimes] = useState({});

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data/blog-posts.json');

        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }

        const data = await response.json();
        setPosts(data);

        // Fetch individual posts to calculate read times
        const readTimes = {};
        await Promise.all(
          data.map(async (post) => {
            try {
              const postResponse = await fetch(`/data/blog-posts/${post.slug}.json`);
              const postData = await postResponse.json();

              // Calculate read time from content
              const text = postData.content
                .filter((block) => block.type === 'paragraph' || block.type === 'heading')
                .map((block) => block.text)
                .join(' ');

              // Simple word count calculation (250 words per minute)
              const words = text.split(/\s+/).length;
              const minutes = Math.ceil(words / 250) || 1;
              readTimes[post.slug] = minutes;
            } catch (err) {
              // If post fetch fails, default to 5 minutes
              readTimes[post.slug] = 5;
            }
          }),
        );

        setPostReadTimes(readTimes);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4" />
          <p className="text-gray-400">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">Error loading blog posts</p>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-cyan-400 mb-4">Blog</h1>
          </div>

          {/* Blog Grid */}
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <BlogCard
                  key={post.id}
                  post={post}
                  readTime={postReadTimes[post.slug] || 5}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default BlogList;
